import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import VoiceOrb from "@/components/VoiceOrb";
import DraftResumeSheet from "@/components/shared/DraftResumeSheet";
import { ErrorState } from "@/components/shared/StateViews";
import { startAvatarCreation, discardDraft } from "@/lib/avatarFlow";
import { friendlyError } from "@/lib/errors";
import { toast } from "sonner";

// Collapsed 1-tap avatar start (FR29 / Story 4.4). On mount: if a draft exists,
// offer continue/discard; otherwise create a draft and go straight to the builder
// in voice mode. Powers both /avatars/new and /onboarding/avatar.
export default function AvatarStartFlow({ eyebrow, title, subtitle, testid }) {
  const nav = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | starting | draft | error
  const [draftId, setDraftId] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const ranRef = useRef(false);

  const begin = async () => {
    setStatus("starting");
    setErrorMsg(null);
    try {
      // startAvatarCreation navigates away on success (component unmounts).
      await startAvatarCreation({
        nav,
        onDraftExists: (id) => {
          setDraftId(id);
          setStatus("draft");
        },
      });
    } catch (err) {
      setErrorMsg(friendlyError(err));
      setStatus("error");
    }
  };

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    (async () => {
      try {
        const { data } = await api.get("/avatars", { params: { status: "draft" } });
        if (data && data.length > 0) {
          setDraftId(data[0].id);
          setStatus("draft");
        } else {
          await begin();
        }
      } catch (err) {
        setErrorMsg(friendlyError(err));
        setStatus("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const discardAndRestart = async () => {
    try {
      await discardDraft();
      setDraftId(null);
      setStatus("checking");
      await begin();
    } catch (err) {
      toast.error(friendlyError(err));
    }
  };

  return (
    <div className="min-h-screen bg-brand-bg" data-testid={testid}>
      <AppHeader />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24 pb-24 md:pb-24">
        <div className="text-center">
          <div className="flex justify-center mb-7">
            <VoiceOrb state="listening" size={110} />
          </div>
          <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-ui mb-2">
            {eyebrow}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-brand-ink mb-3 leading-tight">
            {title}
          </h1>
          <p className="text-brand-ink2 text-[15px] max-w-md mx-auto mb-8">
            {subtitle}
          </p>

          {status === "error" ? (
            <div className="max-w-md mx-auto">
              <ErrorState message={errorMsg} onRetry={begin} testid="avatar-start-error" />
            </div>
          ) : (
            <div
              className="inline-flex items-center gap-2 text-[13px] text-brand-ink2 font-ui"
              data-testid="avatar-start-loading"
            >
              <span className="w-2 h-2 rounded-full bg-brand-rose animate-pulse" />
              Preparando seu avatar...
            </div>
          )}
        </div>
      </div>

      <DraftResumeSheet
        draftId={draftId}
        open={status === "draft"}
        onOpenChange={(o) => {
          // Closing the sheet without choosing continues the existing draft.
          if (!o && draftId) nav(`/avatars/${draftId}/builder?mode=voice`);
        }}
        onContinue={() => nav(`/avatars/${draftId}/builder?mode=voice`)}
        onDiscard={discardAndRestart}
      />
    </div>
  );
}
